/**
 * Athletes API - Consolidated data with inline multilingual support
 * All athlete data and translations in a single file
 */

// ============================================================================
// CONSTANTS - Common terms extracted for reuse
// ============================================================================

export const PLACES = {
  ZIANO_TN: {
    it: "Ziano di Fiemme (TN)",
    en: "Ziano di Fiemme (TN)",
    de: "Ziano di Fiemme (TN)"
  },
  CAVALESE_TN: {
    it: "Cavalese (TN)",
    en: "Cavalese (TN)",
    de: "Cavalese (TN)"
  }
};

export const SPORTS = {
  CROSS_COUNTRY: {
    it: "Sci di Fondo",
    en: "Cross-Country Skiing",
    de: "Skilanglauf"
  },
  BIATHLON: {
    it: "Biathlon",
    en: "Biathlon",
    de: "Biathlon"
  },
  SNOWBOARD: {
    it: "Snowboard",
    en: "Snowboard",
    de: "Snowboard"
  },
  PARALYMPIC: {
    it: "Sci di Fondo Paralimpico",
    en: "Para Nordic Skiing",
    de: "Paralympischer Nordischer Skisport"
  },
  ALPINE: {
    it: "Sci Alpino",
    en: "Alpine Skiing",
    de: "Ski Alpin"
  }
};

// ============================================================================
// UI TRANSLATIONS - Common labels and text
// ============================================================================

export const translations = {
  "site.title": {
    it: "Ziano presenta i suoi campioni",
    en: "Ziano presents its champions",
    de: "Ziano präsentiert seine Champions"
  },
  "home.subtitle": {
    it: "I Nostri Campioni Olimpici",
    en: "Our Olympic Champions",
    de: "Unsere Olympiasieger"
  },
  "home.endphrase": {
    it: "Ziano di Fiemme è un caso più unico che raro: un piccolo paese che vanta una densità di olimpionici per abitante probabilmente tra le più alte al mondo.",
    en: "Ziano di Fiemme is a unique case: a small village that boasts one of the highest densities of Olympians per inhabitant in the world.",
    de: "Ziano di Fiemme ist ein einzigartiger Fall: ein kleines Dorf, das wahrscheinlich eine der weltweit höchsten Dichten an Olympioniken pro Einwohner aufweist."
  },
  "home.endphrase2": {
    it: "La percentuale di medaglie olimpiche pro capite di questo comune è talmente alta che, se Ziano fosse una nazione indipendente, sarebbe ai vertici del medagliere mondiale in rapporto alla popolazione.",
    en: "The percentage of Olympic medals per capita of this municipality is so high that, if Ziano were an independent nation, it would be at the top of the world medal table in proportion to its population.",
    de: "Der Anteil an olympischen Medaillen pro Kopf in dieser Gemeinde ist so hoch, dass Ziano, wäre es eine unabhängige Nation, im Verhältnis zur Bevölkerung an der Spitze des weltweiten Medaillenspiegels stehen würde."
  },
  "home.description": {
    it: "Dieci atleti straordinari che hanno portato il nome di Ziano di Fiemme sui palcoscenici olimpici più prestigiosi del mondo.",
    en: "Ten extraordinary athletes who brought the name of Ziano di Fiemme to the world's most prestigious Olympic stages.",
    de: "Zehn außergewöhnliche Athleten, die den Namen Ziano di Fiemme auf die prestigeträchtigsten olympischen Bühnen der Welt gebracht haben."
  },
  "home.instruction": {
    it: "Clicca su un atleta per scoprire la sua storia.",
    en: "Click on an athlete to discover their story.",
    de: "Klicken Sie auf einen Athleten, um seine Geschichte zu entdecken."
  },
  "common.birthDate": {
    it: "Data di nascita",
    en: "Date of birth",
    de: "Geburtsdatum"
  },
  "common.birthPlace": {
    it: "Luogo di nascita",
    en: "Place of birth",
    de: "Geburtsort"
  },
  "common.curiosities": {
    it: "Curiosità",
    en: "Curiosities",
    de: "Kuriositäten"
  },
  "common.mainResults": {
    it: "Risultati principali",
    en: "Main results",
    de: "Hauptergebnisse"
  },
  "detail.notFound": {
    it: "Atleta non trovato",
    en: "Athlete not found",
    de: "Athlet nicht gefunden"
  },
  "detail.backHome": {
    it: "Torna alla home",
    en: "Back to Home",
    de: "Zurück zur Startseite"
  },
  "detail.allAthletes": {
    it: "Tutti gli atleti",
    en: "All Athletes",
    de: "Alle Athleten"
  },
  "detail.biography": {
    it: "Biografia",
    en: "Biography",
    de: "Biografie"
  },
  "detail.curiosities": {
    it: "Curiosità",
    en: "Curiosities",
    de: "Kuriositäten"
  },
  "detail.olympicParticipations": {
    it: "Partecipazioni Olimpiche",
    en: "Olympic Participations",
    de: "Olympische Teilnahmen"
  },
  "detail.otherResults": {
    it: "Partecipazioni ai campionati del mondo",
    en: "World Championships participations",
    de: "Teilnahmen an Weltmeisterschaften"
  },
  "medals.gold": {
    it: "Oro",
    en: "Gold",
    de: "Gold"
  },
  "medals.silver": {
    it: "Argento",
    en: "Silver",
    de: "Silber"
  },
  "medals.bronze": {
    it: "Bronzo",
    en: "Bronze",
    de: "Bronze"
  },
  "detail.footer": {
    it: "Comune di Ziano di Fiemme",
    en: "Municipality of Ziano di Fiemme",
    de: "Gemeinde Ziano di Fiemme"
  },
  "table.event": {
    it: "Evento",
    en: "Event",
    de: "Veranstaltung"
  },
  "table.year": {
    it: "Anno",
    en: "Year",
    de: "Jahr"
  },
  "table.location": {
    it: "Luogo",
    en: "Location",
    de: "Ort"
  },
  "table.discipline": {
    it: "Disciplina",
    en: "Discipline",
    de: "Disziplin"
  },
  "table.result": {
    it: "Risultato",
    en: "Result",
    de: "Ergebnis"
  },
  "event.olympics": {
    it: "Olimpiadi",
    en: "Olympics",
    de: "Olympische Spiele"
  },
  "event.paralympics": {
    it: "ParaOlimpiadi",
    en: "Paralympics",
    de: "Paralympics"
  },
  "event.worldChampionships": {
    it: "Campionati mondiali",
    en: "World Championships",
    de: "Weltmeisterschaften"
  },
  "event.worldChampionshipsFull": {
    it: "Campionati del mondo",
    en: "World Championships",
    de: "Weltmeisterschaften"
  },
  "event.europeanChampionships": {
    it: "Campionati europei di sci nordico paralimpico",
    en: "European Paralympic Nordic Skiing Championships",
    de: "Europameisterschaften im Paralympischen Nordischen Skisport"
  }
};

// ============================================================================
// ATHLETES DATA - All athlete data with inline multilingual content
// ============================================================================

const athletesData = [

  // ========== GIUSEPPE ZANON ==========
  {
    id: "3",
    name: "Giuseppe Zanon",
    sport: SPORTS.CROSS_COUNTRY,
    birthDate: {
      it: "",
      en: "",
      de: ""
    },
    birthPlace: PLACES.ZIANO_TN,
    photo_url: "/resources/Giuseppe_Zanon.webp",
    medals: { gold: 0, silver: 0, bronze: 0 },
    biography: {
      it: "Atleta olimpico di St. Moritz 1928, noto per il suo rifiuto alla tessera del partito fascista nel 1932.",
      en: "Olympic athlete at St. Moritz 1928, known for his refusal of the fascist party card in 1932.",
      de: "Olympia-Teilnehmer in St. Moritz 1928, bekannt für seine Weigerung der faschistischen Parteikarte 1932."
    },
    olympic_games: [      
      { year: 1932, location: "Lake Placid, USA", results: { it: "Selezionato (non partito)", en: "Selected (did not compete)", de: "Ausgewählt (nicht gestartet)" } }
    ],
    achievements: [
      { it: "Campionati Italiani: 50 km - 5 vittorie (1953, 1954, 1955, 1956, 1957, 1959)", en: "Italian Championships: 50 km - 5 wins (1953, 1954, 1955, 1956, 1957, 1959)", de: "Italienische Meisterschaften: 50 km - 5 Siege (1953, 1954, 1955, 1956, 1957, 1959)" },
      { it: "Campionati Italiani: 15 km - 3 vittorie (1953, 1955, 1956)", en: "Italian Championships: 15 km - 3 wins (1953, 1955, 1956)", de: "Italienische Meisterschaften: 15 km - 3 Siege (1953, 1955, 1956)" },
      { it: "Campionati Italiani: 30 km - 3 vittorie (1953, 1955, 1959)", en: "Italian Championships: 30 km - 3 wins (1953, 1955, 1959)", de: "Italienische Meisterschaften: 30 km - 3 Siege (1953, 1955, 1959)" },
      { it: "Campionati Italiani: 18 km - 2 vittorie (1949, 1952)", en: "Italian Championships: 18 km - 2 wins (1949, 1952)", de: "Italienische Meisterschaften: 18 km - 2 Siege (1949, 1952)" }
    ],
    curiosities: [
      {
        title: {
          it: "Il rifiuto al fascismo (1932):",
          en: "The refusal of fascism (1932):",
          de: "Die Ablehnung des Faschismus (1932):"
        },
        text: {
          it: "Questo è l'aneddoto più potente. Giuseppe Zanon (classe 1901), fu selezionato per le olimpiadi di Lake Placid 1932. Arrivato al porto di Genova per imbarcarsi per l'America, un gerarca fascista controllò i documenti e notò che Zanon non aveva la tessera del partito. Alla richiesta di spiegazioni, Zanon rispose in dialetto: \"Nó la àe e gnan nó la vöi\" (\"Non ce l'ho e nemmeno la voglio\"). Risultato: fu lasciato a terra, niente America e niente Olimpiadi, solo un biglietto del treno per tornare a casa. Un esempio di integrità morale rarissimo.",
          en: "This is the most powerful anecdote. Giuseppe Zanon (born 1901) was selected for the Lake Placid 1932 Olympics. Upon arriving at the port of Genoa to board for America, a fascist official checked his documents and noticed that Zanon did not have the party card. When asked for an explanation, Zanon replied in dialect: \"Nó la àe e gnan nó la vöi\" (\"I don't have it and I don't want it either\"). Result: he was left behind, no America and no Olympics, just a train ticket home. A rare example of moral integrity.",
          de: "Dies ist die eindrucksvollste Anekdote. Giuseppe Zanon (Jahrgang 1901) wurde für die Olympischen Spiele 1932 in Lake Placid ausgewählt. Als er im Hafen von Genua ankam, um nach Amerika einzuschiffen, kontrollierte ein faschistischer Funktionär seine Dokumente und bemerkte, dass Zanon keine Parteimitgliedskarte hatte. Auf die Frage nach einer Erklärung antwortete Zanon im Dialekt: \"Nó la àe e gnan nó la vöi\" (\"Ich habe sie nicht und will sie auch nicht\"). Ergebnis: Er wurde zurückgelassen, kein Amerika und keine Olympiade, nur eine Zugfahrkarte nach Hause. Ein seltenes Beispiel moralischer Integrität."
        }
      },
      {
        title: {
          it: "I Campionati italiani:",
          en: "The Italian Championships:",
          de: "Die italienischen Meisterschaften:"
        },
        text: {
          it: "vinse cinque volte i 50 km (1953, 1954, 1955, 1956, 1957, 1959), tre volte i 15 km (1953, 1955, 1956) e i 30 km (1953, 1955, 1959), e due volte i 18 km (1949, 1952)",
          en: "He won the 50 km five times (1953, 1954, 1955, 1956, 1957, 1959), the 15 km three times (1953, 1955, 1956) and the 30 km (1953, 1955, 1959), and the 18 km twice (1949, 1952).",
          de: "Er gewann fünfmal die 50 km (1953, 1954, 1955, 1956, 1957, 1959), dreimal die 15 km (1953, 1955, 1956) und die 30 km (1953, 1955, 1959) und zweimal die 18 km (1949, 1952)."
        }
      }
    ]
  },

  // ========== GIOVANNI PALUSELLI ==========
  {
    id: "4",
    name: "Giovanni Paluselli",
    sport: SPORTS.ALPINE,
    birthDate: {
      it: "",
      en: "",
      de: ""
    },
    birthPlace: PLACES.ZIANO_TN,
    photo_url: "/resources/Giovanni_Paluselli.webp",
    medals: { gold: 0, silver: 0, bronze: 0 },
    biography: {
      it: "Pioniere dello sci di fondo negli anni '30, capace di imprese eroiche con materiali rudimentali.",
      en: "Pioneer of cross-country skiing in the 30s, capable of heroic feats with rudimentary materials.",
      de: "Pionier des Skilanglaufs in den 30er Jahren, fähig zu heldenhaften Taten mit rudimentärem Material."
    },
    olympic_games: [
      { year: 1936, location: "Garmisch-Partenkirchen, GER", results: { it: "Partecipazione", en: "Participation", de: "Teilnahme" } }
    ],
    achievements: [
      { it: "1938 - Coppa del Mondo (Engelberg, CH): Partecipazione", en: "1938 - World Cup (Engelberg, CH): Participation", de: "1938 - Weltcup (Engelberg, CH): Teilnahme" }
    ],
    curiosities: [
      {
        title: {
          it: "Il pioniere dei materiali:",
          en: "The materials pioneer:",
          de: "Der Materialpionier:"
        },
        text: {
          it: "Parliamo di un'epoca (anni '30) in cui lo sci di fondo era avventura pura. Paluselli gareggiava con sci di legno pesantissimi e bastoncini di bambù o nocciolo… era capace di imprese eroiche con materiali rudimentali.",
          en: "We're talking about an era (the 1930s) when cross-country skiing was pure adventure. Paluselli raced with extremely heavy wooden skis and bamboo or hazel poles… he was capable of heroic feats with rudimentary materials.",
          de: "Wir sprechen von einer Ära (den 1930er Jahren), als Skilanglauf reines Abenteuer war. Paluselli fuhr Rennen mit extrem schweren Holzskiern und Bambus- oder Haselstöcken… er war fähig zu heldenhaften Taten mit rudimentärem Material."
        }
      },
      {
        title: {
          it: "La prima alla Belvedere:",
          en: "The premiere at Belvedere:",
          de: "Die Premiere am Belvedere:"
        },
        text: {
          it: "1936 gara di inaugurazione della pista di Ziano, la pista di casa che poi diventerà la mitica Belvedere. Giovanni Paluselli fermò il crono a 1'09\", un tempo record con cui il discesista olimpionico mise dietro di sé gli atleti della Scuola Alpina Guardia di Finanza.",
          en: "1936 inauguration race of the Ziano track, the home track that would later become the legendary Belvedere. Giovanni Paluselli stopped the clock at 1'09\", a record time with which the Olympic downhill skier left behind the athletes of the Alpine School of the Finance Guard.",
          de: "1936 Einweihungsrennen der Ziano-Strecke, der Heimstrecke, die später zum legendären Belvedere werden sollte. Giovanni Paluselli stoppte die Uhr bei 1'09\", einer Rekordzeit, mit der der olympische Abfahrer die Athleten der Alpinen Schule der Finanzwache hinter sich ließ."
        }
      },
      {
        title: {
          it: "Il primato:",
          en: "The record:",
          de: "Der Rekord:"
        },
        text: {
          it: "Insieme al fratello Alfredo, Giovanni ha segnato una pietra miliare nel turismo invernale italiano. Fondarono infatti la prima scuola di sci nelle Dolomiti al Passo Rolle nella storica Capanna Cervino di cui poi ne riscatterà la proprietà per gestirla fino agli anni '90.",
          en: "Together with his brother Alfredo, Giovanni marked a milestone in Italian winter tourism. In fact, they founded the first ski school in the Dolomites at Passo Rolle in the historic Capanna Cervino, of which he later redeemed the property to manage it until the 90s.",
          de: "Zusammen mit seinem Bruder Alfredo markierte Giovanni einen Meilenstein im italienischen Wintertourismus. Tatsächlich gründeten sie die erste Skischule in den Dolomiten am Rollepass in der historischen Capanna Cervino, deren Eigentum er später zurückkaufte, um sie bis in die 90er Jahre zu führen."
        }
      },
      {
        title: {
          it: "Il Cinema:",
          en: "Cinema:",
          de: "Das Kino:"
        },
        text: {
          it: "Giovanni Paluselli non fu solo atleta. Nel 1941 ha partecipato alla produzione del cortometraggio 'Rocciatori ed Aquile' girato sulle Dolomiti e negli anni seguenti sarà la controfigura dell'attore americano Kirk Douglas.",
          en: "Giovanni Paluselli was not just an athlete. In 1941 he participated in the production of the short film 'Rocciatori ed Aquile' shot in the Dolomites and in the following years he was the stunt double for the American actor Kirk Douglas.",
          de: "Giovanni Paluselli war nicht nur Athlet. 1941 nahm er an der Produktion des Kurzfilms 'Rocciatori ed Aquile' teil, der in den Dolomiten gedreht wurde, und in den folgenden Jahren war er das Stunt-Double für den amerikanischen Schauspieler Kirk Douglas."
        }
      }
    ]
  },

  // ========== FEDERICO DEFLORIAN ==========
  {
    id: "5",
    name: "Federico Deflorian",
    sport: SPORTS.CROSS_COUNTRY,
    birthDate: {
      it: "9 giugno 1921",
      en: "June 9, 1921",
      de: "9. Juni 1921"
    },
    birthPlace: PLACES.ZIANO_TN,
    photo_url: "/resources/Federico_Deflorian.webp",
    medals: { gold: 0, silver: 0, bronze: 0 },
    biography: {
      it: "Detto 'L'Angelo delle Nevi', partecipò alle Olimpiadi di St. Moritz e Oslo. Pioniere dell'insegnamento in Spagna.",
      en: "Called 'The Snow Angel', he participated in the St. Moritz and Oslo Olympics. Pioneer of teaching in Spain.",
      de: "Genannt 'Der Schneeengel', nahm an den Olympischen Spielen in St. Moritz und Oslo teil. Pionier des Unterrichts in Spanien."
    },
    olympic_games: [
      { year: 1952, location: "Oslo, NOR", results: { it: "18 km: 19°", en: "18 km: 19th", de: "18 km: 19." } },
      { year: 1952, location: "Oslo, NOR", results: { it: "Staffetta: 6° (con Delladio, Anderlini, Perruchon)", en: "Relay: 6th (with Delladio, Anderlini, Perruchon)", de: "Staffel: 6. (mit Delladio, Anderlini, Perruchon)" } },
      { year: 1956, location: "Cortina, ITA", results: { it: "15 km: 17°", en: "15 km: 17th", de: "15 km: 17." } },
      { year: 1956, location: "Cortina, ITA", results: { it: "30 km: 13°", en: "30 km: 13th", de: "30 km: 13." } },
      { year: 1956, location: "Cortina, ITA", results: { it: "Staffetta: 5° (con Fattor, Compagnoni, Chatrian)", en: "Relay: 5th (with Fattor, Compagnoni, Chatrian)", de: "Staffel: 5. (mit Fattor, Compagnoni, Chatrian)" } },
      { year: 1960, location: "Squaw Valley, USA", results: { it: "50 km: 16°", en: "50 km: 16th", de: "50 km: 16." } }
    ],
    achievements: [
      { it: "1954 - Mondiali (Falun, SWE): 15 km - 37°", en: "1954 - World Championships (Falun, SWE): 15 km - 37th", de: "1954 - WM (Falun, SWE): 15 km - 37." },
      { it: "1954 - Mondiali (Falun, SWE): 30 km - 20°", en: "1954 - World Championships (Falun, SWE): 30 km - 20th", de: "1954 - WM (Falun, SWE): 30 km - 20." },
      { it: "1954 - Mondiali (Falun, SWE): Staffetta - 5°", en: "1954 - World Championships (Falun, SWE): Relay - 5th", de: "1954 - WM (Falun, SWE): Staffel - 5." },
      { it: "1958 - Mondiali (Lahti, FIN): 30 km - 34°", en: "1958 - World Championships (Lahti, FIN): 30 km - 34th", de: "1958 - WM (Lahti, FIN): 30 km - 34." },
      { it: "1958 - Mondiali (Lahti, FIN): 50 km - 19°", en: "1958 - World Championships (Lahti, FIN): 50 km - 19th", de: "1958 - WM (Lahti, FIN): 50 km - 19." },
      { it: "1958 - Mondiali (Lahti, FIN): Staffetta - 5°", en: "1958 - World Championships (Lahti, FIN): Relay - 5th", de: "1958 - WM (Lahti, FIN): Staffel - 5." }
    ],
    curiosities: [
      {
        title: {
          it: "La scelta civile:",
          en: "The civil choice:",
          de: "Die zivile Entscheidung:"
        },
        text: {
          it: "Nel dopoguerra, quando i gruppi sportivi militari (come Fiamme Oro e Fiamme Gialle) iniziarono a dominare lo sci di fondo italiano offrendo stipendi e strutture, Deflorian scelse di gareggiare per la sua società civile locale, l'U.S. Cauriol.",
          en: "In the post-war period, when military sports groups (such as Fiamme Oro and Fiamme Gialle) began to dominate Italian cross-country skiing by offering salaries and facilities, Deflorian chose to compete for his local civilian club, U.S. Cauriol.",
          de: "In der Nachkriegszeit, als militärische Sportgruppen (wie Fiamme Oro und Fiamme Gialle) begannen, den italienischen Skilanglauf zu dominieren, indem sie Gehälter und Einrichtungen anboten, entschied sich Deflorian, für seinen lokalen zivilen Verein U.S. Cauriol anzutreten."
        }
      },
      {
        title: {
          it: "L'Angelo delle Nevi:",
          en: "The Snow Angel:",
          de: "Der Schneeengel:"
        },
        text: {
          it: "Attivo negli anni '50 (Olimpiadi di Oslo '52 e Cortina '56), era soprannominato così per il suo stile tecnico impeccabile, elegante e leggero, che contrastava con la forza bruta di altri concorrenti.",
          en: "Active in the 1950s (Oslo '52 and Cortina '56 Olympics), he was nicknamed this for his impeccable, elegant and light technical style, which contrasted with the brute force of other competitors.",
          de: "Aktiv in den 1950er Jahren (Olympische Spiele Oslo '52 und Cortina '56), erhielt er diesen Spitznamen wegen seines tadellosen, eleganten und leichten technischen Stils, der im Kontrast zur rohen Kraft anderer Konkurrenten stand."
        }
      },
      {
        title: {
          it: "Curiosità sui materiali:",
          en: "Curiosity about materials:",
          de: "Kuriosität über Materialien:"
        },
        text: {
          it: "A differenza degli atleti moderni che hanno decine di paia di sci, atleti come Federico gareggiavano con sci di legno pesantissimi che dovevano sciolinare personalmente con mix di resine e sostanze che oggi sarebbero considerate alchimia pura. Rappresenta l'epoca \"eroica\" dello sci di Ziano.",
          en: "Unlike modern athletes who have dozens of pairs of skis, athletes like Federico raced with extremely heavy wooden skis that they had to wax personally with mixes of resins and substances that today would be considered pure alchemy. He represents the \"heroic\" era of Ziano skiing.",
          de: "Im Gegensatz zu modernen Athleten, die Dutzende von Skipaaren haben, fuhren Athleten wie Federico Rennen mit extrem schweren Holzskiern, die sie persönlich mit Mischungen aus Harzen und Substanzen wachsen mussten, die heute als reine Alchemie gelten würden. Er repräsentiert die \"heroische\" Ära des Skisports in Ziano."
        }
      },
      {
        title: {
          it: "L'esilio in Spagna:",
          en: "Exile in Spain:",
          de: "Das Exil in Spanien:"
        },
        text: {
          it: "Una chicca poco nota: dopo le Olimpiadi del 1948, deluso dal trattamento economico riservato agli atleti italiani (ricevevano solo una piccola diaria, non uno stipendio), accettò una proposta per andare ad allenare la nazionale di sci della Spagna. Fu un pioniere dell'insegnamento tecnico all'estero, in un paese dove lo sci di fondo era ancora \"preistorico\".",
          en: "A little-known tidbit: after the 1948 Olympics, disappointed by the economic treatment given to Italian athletes (they only received a small daily allowance, not a salary), he accepted a proposal to train the Spanish national ski team. He was a pioneer of technical teaching abroad, in a country where cross-country skiing was still \"prehistoric\".",
          de: "Ein wenig bekanntes Detail: Nach den Olympischen Spielen 1948, enttäuscht von der wirtschaftlichen Behandlung italienischer Athleten (sie erhielten nur ein kleines Tagegeld, kein Gehalt), nahm er ein Angebot an, die spanische Skinationalmannschaft zu trainieren. Er war ein Pionier des technischen Unterrichts im Ausland, in einem Land, wo Skilanglauf noch \"prähistorisch\" war."
        }
      },
      {
        title: {
          it: "Federico \"Il Grande\":",
          en: "Federico \"The Great\":",
          de: "Federico \"Der Große\":"
        },
        text: {
          it: "Lo chiamavano \"Il grande\" per le tante vittorie, non certamente per la statura. Più piccolo della media dei fondisti, ma dal fisico possente, dominava le gare nazionali (16 titoli italiani) tanto da guadagnarsi questo soprannome imperiale.",
          en: "They called him \"The Great\" for his many victories, certainly not for his stature. Smaller than the average cross-country skier, but with a powerful physique, he dominated national races (16 Italian titles) so much that he earned this imperial nickname.",
          de: "Man nannte ihn \"Der Große\" wegen seiner vielen Siege, sicherlich nicht wegen seiner Statur. Kleiner als der durchschnittliche Langläufer, aber mit einem kräftigen Körperbau, dominierte er die nationalen Rennen (16 italienische Titel) so sehr, dass er sich diesen kaiserlichen Spitznamen verdiente."
        }
      }
    ]
  },

  // ========== GIULIO DEFLORIAN ==========
  {
    id: "6",
    name: "Giulio Deflorian",
    sport: SPORTS.CROSS_COUNTRY,
    birthDate: {
      it: "13 gennaio 1936",
      en: "January 13, 1936",
      de: "13. Januar 1936"
    },
    birthPlace: PLACES.ZIANO_TN,
    photo_url: "/resources/Giulio_Deflorian.webp",
    medals: { gold: 0, silver: 0, bronze: 0 },
    biography: {
      it: "Primo italiano a vincere una medaglia ai Mondiali di sci nordico (Bronzo a Zakopane 1962).",
      en: "First Italian to win a medal at the Nordic Ski World Championships (Bronze in Zakopane 1962).",
      de: "Erster Italiener, der eine Medaille bei den Nordischen Ski-Weltmeisterschaften gewann (Bronze in Zakopane 1962)."
    },
    olympic_games: [
      { year: 1960, location: "Squaw Valley, USA", results: { it: "15 km: 14°", en: "15 km: 14th", de: "15 km: 14." } },
      { year: 1960, location: "Squaw Valley, USA", results: { it: "30 km: 11°", en: "30 km: 11th", de: "30 km: 11." } },
      { year: 1960, location: "Squaw Valley, USA", results: { it: "Staffetta: 5°", en: "Relay: 5th", de: "Staffel: 5." } },
      { year: 1964, location: "Innsbruck, AUT", results: { it: "15 km: 18°", en: "15 km: 18th", de: "15 km: 18." } },
      { year: 1964, location: "Innsbruck, AUT", results: { it: "Staffetta: 5°", en: "Relay: 5th", de: "Staffel: 5." } },
      { year: 1968, location: "Grenoble, FRA", results: { it: "15 km: 15°", en: "15 km: 15th", de: "15 km: 15." } },
      { year: 1968, location: "Grenoble, FRA", results: { it: "30 km: 5°", en: "30 km: 5th", de: "30 km: 5." } },
      { year: 1968, location: "Grenoble, FRA", results: { it: "Staffetta: 6°", en: "Relay: 6th", de: "Staffel: 6." } }
    ],
    achievements: [
      { it: "1962 - Mondiali (Zakopane, POL): 30 km - 3° (Bronzo)", en: "1962 - World Championships (Zakopane, POL): 30 km - 3rd (Bronze)", de: "1962 - WM (Zakopane, POL): 30 km - 3. (Bronze)" },
      { it: "1966 - Mondiali (Oslo, NOR): Staffetta - 3° (Bronzo)", en: "1966 - World Championships (Oslo, NOR): Relay - 3rd (Bronze)", de: "1966 - WM (Oslo, NOR): Staffel - 3. (Bronze)" }
    ],
    curiosities: [
      {
        title: {
          it: "L'uomo che ruppe il tabù:",
          en: "The man who broke the taboo:",
          de: "Der Mann, der das Tabu brach:"
        },
        text: {
          it: "Giulio Deflorian è una leggenda assoluta. Fu il primo italiano in assoluto (e primo non scandinavo/russo) a vincere una medaglia ai Mondiali di sci nordico (Bronzo a Zakopane 1962, PRIMA MEDAGLIA ITALIANA DEL DOPOGUERRA). Prima di lui, si pensava che gli italiani non avessero il DNA per battere i nordici.",
          en: "Giulio Deflorian is an absolute legend. He was the first Italian ever (and first non-Scandinavian/Russian) to win a medal at the Nordic Ski World Championships (Bronze at Zakopane 1962, FIRST ITALIAN POST-WAR MEDAL). Before him, it was thought that Italians didn't have the DNA to beat the Nordics.",
          de: "Giulio Deflorian ist eine absolute Legende. Er war der erste Italiener überhaupt (und erste Nicht-Skandinavier/Russe), der eine Medaille bei den Nordischen Ski-Weltmeisterschaften gewann (Bronze in Zakopane 1962, ERSTE ITALIENISCHE MEDAILLE DER NACHKRIEGSZEIT). Vor ihm dachte man, dass Italiener nicht die DNA hätten, die Nordländer zu schlagen."
        }
      },
      {
        title: {
          it: "L'aneddoto della povertà:",
          en: "The poverty anecdote:",
          de: "Die Armuts-Anekdote:"
        },
        text: {
          it: "Quando arrivò a Oslo nel '66, la stampa scandinava descrisse gli italiani come \"poverelli\" con equipaggiamento inferiore. Deflorian, piccolo di statura ma con un motore enorme, zittì la critica nordica. Si racconta che i norvegesi rimasero così scioccati dal vedere un \"latino\" sul podio nella loro disciplina sacra che iniziarono a studiare i suoi metodi di allenamento.",
          en: "When he arrived in Oslo in '66, the Scandinavian press described the Italians as \"poor fellows\" with inferior equipment. Deflorian, small in stature but with a huge engine, silenced Nordic critics. It is said that the Norwegians were so shocked to see a \"Latin\" on the podium in their sacred discipline that they began to study his training methods.",
          de: "Als er 1966 in Oslo ankam, beschrieb die skandinavische Presse die Italiener als \"arme Kerle\" mit minderwertiger Ausrüstung. Deflorian, klein von Statur aber mit einem enormen Motor, brachte die nordischen Kritiker zum Schweigen. Es heißt, die Norweger waren so schockiert, einen \"Lateiner\" auf dem Podium in ihrer heiligen Disziplin zu sehen, dass sie begannen, seine Trainingsmethoden zu studieren."
        }
      },
      {
        title: {
          it: "L'arrabbiatura di Grenoble:",
          en: "The Grenoble anger:",
          de: "Die Wut von Grenoble:"
        },
        text: {
          it: "Alle Olimpiadi del 1968 (quelle vinte da Nones), Giulietto arrivò 5°, ma rimase celebre la sua rabbia perché fu fatto partire nel \"quarto gruppo\" di merito, trovando condizioni di neve ormai rovinate che, a suo dire, gli costarono una medaglia certa.",
          en: "At the 1968 Olympics (those won by Nones), Giulietto finished 5th, but his anger remained famous because he was made to start in the \"fourth merit group\", finding snow conditions already ruined which, in his opinion, cost him a certain medal.",
          de: "Bei den Olympischen Spielen 1968 (die von Nones gewonnen wurden) wurde Giulietto 5., aber seine Wut blieb berühmt, weil er in der \"vierten Leistungsgruppe\" starten musste und bereits ruinierte Schneebedingungen vorfand, die ihn seiner Meinung nach eine sichere Medaille kosteten."
        }
      },
      {
        title: {
          it: "I caporali sui colonnelli:",
          en: "Corporals over colonels:",
          de: "Korporale über Oberste:"
        },
        text: {
          it: "Con particolare orgoglio si ricorda la celebre frase di Nando Martellini, giornalista della Gazzetta dello Sport, che a Colle Isarco (Bolzano) commentava la staffetta 3 x 10 km, allorché, all'ultimo chilometro Giulio Deflorian era in testa davanti a tutti: 'E si profila un'altra vittoria, forse l'ultima, dei caporali sui colonnelli. La Cauriol di Ziano di Fiemme vince la staffetta, valida per l'assegnazione del titolo tricolore per società con la squadra composta da Federico Deflorian, Giuseppe Steiner e Giulietto Deflorian'.",
          en: "With particular pride, we remember the famous phrase of Nando Martellini, journalist of the Gazzetta dello Sport, who in Colle Isarco (Bolzano) commented on the 3 x 10 km relay, when, at the last kilometer Giulio Deflorian was in the lead in front of everyone: 'And another victory is looming, perhaps the last, of the corporals over the colonels. The Cauriol of Ziano di Fiemme wins the relay, valid for the assignment of the tricolor title for clubs with the team composed of Federico Deflorian, Giuseppe Steiner and Giulietto Deflorian'.",
          de: "Mit besonderem Stolz erinnert man sich an den berühmten Satz von Nando Martellini, Journalist der Gazzetta dello Sport, der in Colle Isarco (Bozen) die 3 x 10 km Staffel kommentierte, als Giulio Deflorian auf dem letzten Kilometer vor allen in Führung lag: 'Und ein weiterer Sieg zeichnet sich ab, vielleicht der letzte, der Korporale über die Oberste. Die Cauriol von Ziano di Fiemme gewinnt die Staffel, gültig für die Vergabe des Trikolore-Titels für Vereine mit der Mannschaft bestehend aus Federico Deflorian, Giuseppe Steiner und Giulietto Deflorian'."
        }
      }
    ]
  },

  // ========== MARCO ZANON ==========
  {
    id: "1",
    name: "Marco Zanon",
    sport: SPORTS.BIATHLON,
    birthDate: {
      it: "6 maggio 1956",
      en: "May 6, 1956",
      de: "6. Mai 1956"
    },
    birthPlace: PLACES.ZIANO_TN,
    photo_url: "/resources/Marco_Zanon.webp",
    medals: { gold: 0, silver: 0, bronze: 0 },
    biography: {
      it: "Marco Zanon fu uno dei primi atleti della valle a specializzarsi nel biathlon, in un'epoca di grande transizione tecnologica. Partecipò alle Olimpiadi di Lake Placid 1980.",
      en: "Marco Zanon was one of the first athletes in the valley to specialize in biathlon, in a time of great technological transition. Participated in Lake Placid 1980.",
      de: "Marco Zanon war einer der ersten Athleten des Tals, der sich auf Biathlon spezialisierte. Teilnehmer in Lake Placid 1980."
    },
    olympic_games: [
      { year: 1984, location: "Sarajevo, YUG", results: { it: "Biathlon 20 km: 19°", en: "Biathlon 20 km: 19th", de: "Biathlon 20 km: 19." } }
    ],
    achievements: [
      { it: "1981 - Mondiali (Lahti, FIN): Biathlon 20 km - 48°", en: "1981 - World Championships (Lahti, FIN): Biathlon 20 km - 48th", de: "1981 - WM (Lahti, FIN): Biathlon 20 km - 48." },
      { it: "1981 - Mondiali (Lahti, FIN): Staffetta 4X7,5 km - 9°", en: "1981 - World Championships (Lahti, FIN): 4X7.5 km Relay - 9th", de: "1981 - WM (Lahti, FIN): 4X7,5 km Staffel - 9." },
      { it: "1982 - Mondiali (Minsk, BLR): Staffetta 4X7,5 km - 5°", en: "1982 - World Championships (Minsk, BLR): 4X7.5 km Relay - 5th", de: "1982 - WM (Minsk, BLR): 4X7,5 km Staffel - 5." },
      { it: "1982 - Mondiali (Minsk, BLR): 20 km Individuale - 17°", en: "1982 - World Championships (Minsk, BLR): 20 km Individual - 17th", de: "1982 - WM (Minsk, BLR): 20 km Einzel - 17." },
      { it: "1982 - Mondiali (Minsk, BLR): 10 km Sprint - 33°", en: "1982 - World Championships (Minsk, BLR): 10 km Sprint - 33rd", de: "1982 - WM (Minsk, BLR): 10 km Sprint - 33." }
    ],
    curiosities: [
      {
        title: {
          it: "Il pioniere del Biathlon:",
          en: "The Biathlon pioneer:",
          de: "Der Biathlon-Pionier:"
        },
        text: {
          it: "Partecipò alle Olimpiadi di Lake Placid 1980. La curiosità sta nel fatto che Marco Zanon fu uno dei primi atleti della valle a specializzarsi nel biathlon, in un'epoca di grande transizione tecnologica.",
          en: "He participated in the Lake Placid 1980 Olympics. The curiosity lies in the fact that Marco Zanon was one of the first athletes in the valley to specialize in biathlon, during a time of great technological transition.",
          de: "Er nahm an den Olympischen Spielen 1980 in Lake Placid teil. Die Kuriosität liegt in der Tatsache, dass Marco Zanon einer der ersten Athleten des Tals war, der sich auf Biathlon spezialisierte, in einer Zeit großer technologischer Veränderungen."
        }
      },
      {
        title: {
          it: "Il calibro grosso:",
          en: "The large caliber:",
          de: "Das Großkaliber:"
        },
        text: {
          it: "A quei tempi si sparava ancora con il \"grosso calibro\" (veri fucili da guerra modificati) su distanze maggiori rispetto a oggi (spesso 150 metri). Il passaggio al piccolo calibro (che usiamo oggi) avvenne proprio in quegli anni (1978-1980), rendendo Zanon un testimone oculare del cambiamento epocale di questo sport: da disciplina militare a sport moderno.",
          en: "Back then, they still shot with \"large caliber\" (real modified war rifles) at greater distances than today (often 150 meters). The switch to small caliber (that we use today) happened in those very years (1978-1980), making Zanon an eyewitness to this epochal change in the sport: from military discipline to modern sport.",
          de: "Damals wurde noch mit \"Großkaliber\" (echte modifizierte Kriegsgewehre) auf größere Distanzen als heute geschossen (oft 150 Meter). Der Wechsel zum Kleinkaliber (das wir heute verwenden) erfolgte genau in jenen Jahren (1978-1980), was Zanon zum Augenzeugen dieses epochalen Wandels des Sports machte: von der militärischen Disziplin zum modernen Sport."
        }
      },
      {
        title: {
          it: "Passione senza fine:",
          en: "Endless passion:",
          de: "Unendliche Leidenschaft:"
        },
        text: {
          it: "Marco Zanon si può definire uno dei tecnici più longevi del biathlon italiano, avendo ricoperto ruoli di responsabilità per 13 anni consecutivi a livello nazionale, oltre all'attuale impegno territoriale. E' stato infatti prima allenatore della squadra nazionale Junior dal 1985 al 1994 e poi allenatore e responsabile della Squadra nazionale di Coppa del Mondo dal 1994 al 1998. Dal 1990 al 2000 ha ricoperto il ruolo di responsabile della Scuola Tecnici Federali per il Biathlon e dal 2018 è allenatore del Comitato Trentino di Biathlon",
          en: "Marco Zanon can be defined as one of the longest-serving technicians in Italian biathlon, having held positions of responsibility for 13 consecutive years at the national level, in addition to his current territorial commitment. He was first coach of the Junior national team from 1985 to 1994 and then coach and manager of the World Cup national team from 1994 to 1998. From 1990 to 2000 he held the role of manager of the Federal Technical School for Biathlon and since 2018 he is coach of the Trentino Biathlon Committee.",
          de: "Marco Zanon kann als einer der dienstältesten Techniker im italienischen Biathlon bezeichnet werden, da er 13 Jahre in Folge verantwortungsvolle Positionen auf nationaler Ebene innehatte, zusätzlich zu seinem aktuellen territorialen Engagement. Er war zunächst Trainer der Junioren-Nationalmannschaft von 1985 bis 1994 und dann Trainer und Verantwortlicher der Weltcup-Nationalmannschaft von 1994 bis 1998. Von 1990 bis 2000 bekleidete er die Rolle des Verantwortlichen der Bundestrainerschule für Biathlon und seit 2018 ist er Trainer des Trentiner Biathlon-Komitees."
        }
      }
    ]
  },

  // ========== GIORGIO VANZETTA ==========
  {
    id: "7",
    name: "Giorgio Vanzetta",
    sport: SPORTS.CROSS_COUNTRY,
    birthDate: {
      it: "9 ottobre 1959",
      en: "October 9, 1959",
      de: "9. Oktober 1959"
    },
    birthPlace: PLACES.CAVALESE_TN,
    photo_url: "/resources/Giorgio_Vanzetta.webp",
    medals: { gold: 1, silver: 1, bronze: 2 },
    biography: {
      it: "Membro della leggendaria staffetta che vinse l'Oro a Lillehammer 1994 battendo i norvegesi in casa.",
      en: "Member of the legendary relay team that won Gold at Lillehammer 1994 beating the Norwegians at home.",
      de: "Mitglied der legendären Staffel, die in Lillehammer 1994 Gold gewann und die Norweger zu Hause schlug."
    },
    olympic_games: [
      { year: 1980, location: "Lake Placid, USA", results: { it: "15 km: 34°", en: "15 km: 34th", de: "15 km: 34." } },
      { year: 1980, location: "Lake Placid, USA", results: { it: "Staffetta 4×10 km: 6°", en: "4×10 km Relay: 6th", de: "4×10 km Staffel: 6." } },
      { year: 1984, location: "Sarajevo, YUG", results: { it: "15 km: 14°", en: "15 km: 14th", de: "15 km: 14." } },
      { year: 1984, location: "Sarajevo, YUG", results: { it: "30 km: 24°", en: "30 km: 24th", de: "30 km: 24." } },
      { year: 1984, location: "Sarajevo, YUG", results: { it: "50 km: 30°", en: "50 km: 30th", de: "50 km: 30." } },
      { year: 1984, location: "Sarajevo, YUG", results: { it: "Staffetta 4×10 km: 7°", en: "4×10 km Relay: 7th", de: "4×10 km Staffel: 7." } },
      { year: 1988, location: "Calgary, CAN", results: { it: "15 km: 10°", en: "15 km: 10th", de: "15 km: 10." } },
      { year: 1988, location: "Calgary, CAN", results: { it: "30 km: 5°", en: "30 km: 5th", de: "30 km: 5." } },
      { year: 1988, location: "Calgary, CAN", results: { it: "Staffetta 4×10 km: 5°", en: "4×10 km Relay: 5th", de: "4×10 km Staffel: 5." } },
      { year: 1992, location: "Albertville, FRA", results: { it: "10 km: 7°", en: "10 km: 7th", de: "10 km: 7." } },
      { year: 1992, location: "Albertville, FRA", results: { it: "50 km: 3° (Bronzo)", en: "50 km: 3rd (Bronze)", de: "50 km: 3. (Bronze)" } },
      { year: 1992, location: "Albertville, FRA", results: { it: "10/15 km Pursuit: 3° (Bronzo)", en: "10/15 km Pursuit: 3rd (Bronze)", de: "10/15 km Verfolgung: 3. (Bronze)" } },
      { year: 1992, location: "Albertville, FRA", results: { it: "Staffetta 4×10 km: 2° (Argento)", en: "4×10 km Relay: 2nd (Silver)", de: "4×10 km Staffel: 2. (Silber)" } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "10 km: 15°", en: "10 km: 15th", de: "10 km: 15." } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "10/15 km Pursuit: 9°", en: "10/15 km Pursuit: 9th", de: "10/15 km Verfolgung: 9." } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "30 km: 14°", en: "30 km: 14th", de: "30 km: 14." } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "50 km: 8°", en: "50 km: 8th", de: "50 km: 8." } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "Staffetta 4×10 km: 1° (Oro)", en: "4×10 km Relay: 1st (Gold)", de: "4×10 km Staffel: 1. (Gold)" } }
    ],
    achievements: [
      { it: "1985 - Mondiali (Seefeld, AUT): Staffetta 4×10 km - 2° (Argento)", en: "1985 - World Championships (Seefeld, AUT): 4×10 km Relay - 2nd (Silver)", de: "1985 - WM (Seefeld, AUT): 4×10 km Staffel - 2. (Silber)" },
      { it: "1993 - Mondiali (Falun, SWE): Staffetta 4×10 km - 2° (Argento)", en: "1993 - World Championships (Falun, SWE): 4×10 km Relay - 2nd (Silver)", de: "1993 - WM (Falun, SWE): 4×10 km Staffel - 2. (Silber)" }
    ],
    curiosities: [
      {
        title: {
          it: "\"Il Taciturno\":",
          en: "\"The Silent One\":",
          de: "\"Der Schweigsame\":"
        },
        text: {
          it: "Giorgio era famoso nell'ambiente per essere l'opposto dei media-man moderni. Era soprannominato \"Il taciturno\" o l'uomo di ghiaccio. Non lasciava mai trasparire emozioni, né fatica né gioia sfrenata, il che innervosiva gli avversari che non capivano mai quanto ne avesse ancora.",
          en: "Giorgio was famous in the environment for being the opposite of modern media-men. He was nicknamed \"The Silent One\" or the ice man. He never let emotions show, neither fatigue nor unbridled joy, which annoyed opponents who could never tell how much he had left.",
          de: "Giorgio war in der Szene dafür bekannt, das Gegenteil der modernen Medienmänner zu sein. Er wurde \"Der Schweigsame\" oder der Eismann genannt. Er ließ nie Emotionen erkennen, weder Müdigkeit noch überschwängliche Freude, was die Gegner nervte, die nie wussten, wie viel er noch hatte."
        }
      },
      {
        title: {
          it: "Il \"Motore\" della Staffetta:",
          en: "The Relay \"Engine\":",
          de: "Der \"Motor\" der Staffel:"
        },
        text: {
          it: "È famoso per essere stato uno dei quattro eroi della \"Staffetta dei Miracoli\" a Lillehammer 1994 (Oro contro la Norvegia in casa loro). L'aneddoto più bello riguarda il \"silenzio\": quando l'Italia vinse quell'oro in casa dei norvegesi, lo stadio (che conteneva 100.000 persone) ammutolì.",
          en: "He is famous for being one of the four heroes of the \"Miracle Relay\" at Lillehammer 1994 (Gold against Norway on their home turf). The most beautiful anecdote concerns the \"silence\": when Italy won that gold in the Norwegians' home, the stadium (which held 100,000 people) fell silent.",
          de: "Er ist berühmt dafür, einer der vier Helden der \"Wunderstaffel\" in Lillehammer 1994 (Gold gegen Norwegen auf deren Heimatboden). Die schönste Anekdote betrifft die \"Stille\": Als Italien dieses Gold im Haus der Norweger gewann, verstummte das Stadion (das 100.000 Menschen fasste)."
        }
      },
      {
        title: {
          it: "La curiosità tecnica:",
          en: "The technical curiosity:",
          de: "Die technische Kuriosität:"
        },
        text: {
          it: "Vanzetta fu schierato in terza frazione a passo pattinato (skating) per recuperare il distacco: fece una frazione talmente mostruosa da permettere a Silvio Fauner di partire per l'ultimo giro attaccato al norvegese Daehlie.",
          en: "Vanzetta was fielded in the third leg with skating technique to recover the gap: he made such a monstrous leg that he allowed Silvio Fauner to start the last lap attached to the Norwegian Daehlie.",
          de: "Vanzetta wurde im dritten Abschnitt mit Skating-Technik eingesetzt, um den Rückstand aufzuholen: Er lief einen so monströsen Abschnitt, dass Silvio Fauner die letzte Runde direkt hinter dem Norweger Daehlie starten konnte."
        }
      },
      {
        title: {
          it: "La sfida in famiglia:",
          en: "The family challenge:",
          de: "Die Familien-Herausforderung:"
        },
        text: {
          it: "Si racconta che la rivalità agonistica (sana) con gli altri grandi del fondo italiano (Albarello, De Zolt) fosse vissuta con goliardia, ma Giorgio era sempre quello che parlava meno e \"menava\" di più in allenamento.",
          en: "It is said that the (healthy) competitive rivalry with the other greats of Italian cross-country skiing (Albarello, De Zolt) was experienced with joviality, but Giorgio was always the one who talked less and \"worked harder\" in training.",
          de: "Es heißt, dass die (gesunde) sportliche Rivalität mit den anderen Größen des italienischen Langlaufs (Albarello, De Zolt) mit Ausgelassenheit gelebt wurde, aber Giorgio war immer derjenige, der weniger redete und im Training mehr \"arbeitete\"."
        }
      }
    ]
  },

  // ========== ERNESTO VINANTE ==========
  {
    id: "10",
    name: "Ernesto Vinante",
    sport: SPORTS.PARALYMPIC,
    birthDate: {
      it: "1 giugno 1958",
      en: "June 1, 1958",
      de: "1. Juni 1958"
    },
    birthPlace: PLACES.ZIANO_TN,
    photo_url: "/resources/Ernesto_Vinante.webp",
    medals: { gold: 0, silver: 0, bronze: 1 },
    biography: {
      it: "Straordinario atleta paralimpico, bronzo a Lillehammer 1994.",
      en: "Extraordinary Paralympic athlete, bronze medalist at Lillehammer 1994.",
      de: "Außergewöhnlicher paralympischer Athlet, Bronzemedaillengewinner in Lillehammer 1994."
    },
    olympic_games: [
      { year: 1988, location: "Innsbruck, AUT (Paralympics)", results: { it: "5 km: 12° (Cat LW4)", en: "5 km: 12th (Cat LW4)", de: "5 km: 12. (Kat LW4)" } },
      { year: 1988, location: "Innsbruck, AUT (Paralympics)", results: { it: "15 km: 10° (Cat LW4)", en: "15 km: 10th (Cat LW4)", de: "15 km: 10. (Kat LW4)" } },
      { year: 1994, location: "Lillehammer, NOR (Paralympics)", results: { it: "10 km Free: 3° (Bronzo) (Cat LW2/3/9)", en: "10 km Free: 3rd (Bronze) (Cat LW2/3/9)", de: "10 km Freistil: 3. (Bronze) (Kat LW2/3/9)" } },
      { year: 1994, location: "Lillehammer, NOR (Paralympics)", results: { it: "7,5 km Free: 7°", en: "7.5 km Free: 7th", de: "7,5 km Freistil: 7." } },
      { year: 1994, location: "Lillehammer, NOR (Paralympics)", results: { it: "Staffetta 4×5 km: 6°", en: "4×5 km Relay: 6th", de: "4×5 km Staffel: 6." } },
      { year: 1994, location: "Lillehammer, NOR (Paralympics)", results: { it: "20 km Classic: 10°", en: "20 km Classic: 10th", de: "20 km Klassisch: 10." } },
      { year: 1994, location: "Lillehammer, NOR (Paralympics)", results: { it: "5 km Classic: 9°", en: "5 km Classic: 9th", de: "5 km Klassisch: 9." } },
      { year: 1998, location: "Nagano, JPN (Paralympics)", results: { it: "10 km Free: 4°", en: "10 km Free: 4th", de: "10 km Freistil: 4." } },
      { year: 1998, location: "Nagano, JPN (Paralympics)", results: { it: "5 km Classic: 13°", en: "5 km Classic: 13th", de: "5 km Klassisch: 13." } },
      { year: 1998, location: "Nagano, JPN (Paralympics)", results: { it: "20 km Classic: 22°", en: "20 km Classic: 22nd", de: "20 km Klassisch: 22." } }
    ],
    achievements: [
      { it: "1993 - Europei (Baierbronn, DEU): 5 km Classic - 5°", en: "1993 - European Championships (Baierbronn, DEU): 5 km Classic - 5th", de: "1993 - EM (Baierbronn, DEU): 5 km Klassisch - 5." },
      { it: "1997 - Europei (Tobol'sk, RUS): 20 km Free - 1° (Oro)", en: "1997 - European Championships (Tobol'sk, RUS): 20 km Free - 1st (Gold)", de: "1997 - EM (Tobol'sk, RUS): 20 km Freistil - 1. (Gold)" },
      { it: "1997 - Europei (Tobol'sk, RUS): 7,5 km Biathlon - 1° (Oro)", en: "1997 - European Championships (Tobol'sk, RUS): 7.5 km Biathlon - 1st (Gold)", de: "1997 - EM (Tobol'sk, RUS): 7,5 km Biathlon - 1. (Gold)" },
      { it: "1997 - Europei (Tobol'sk, RUS): 10 km Classic - 5°", en: "1997 - European Championships (Tobol'sk, RUS): 10 km Classic - 5th", de: "1997 - EM (Tobol'sk, RUS): 10 km Klassisch - 5." },
      { it: "1997 - Europei (Tobol'sk, RUS): 5 km Classic - 5°", en: "1997 - European Championships (Tobol'sk, RUS): 5 km Classic - 5th", de: "1997 - EM (Tobol'sk, RUS): 5 km Klassisch - 5." }
    ],
    curiosities: [
      {
        title: {
          it: "La forza di volontà:",
          en: "Willpower:",
          de: "Willenskraft:"
        },
        text: {
          it: "Sebbene l'elenco includa solitamente atleti olimpici, Ernesto Vinante merita una menzione d'onore speciale. È stato un atleta paralimpico straordinario, vincitore di un Bronzo a Lillehammer 1994.",
          en: "Although the list usually includes Olympic athletes, Ernesto Vinante deserves a special honorary mention. He was an extraordinary Paralympic athlete, winner of a Bronze at Lillehammer 1994.",
          de: "Obwohl die Liste normalerweise olympische Athleten enthält, verdient Ernesto Vinante eine besondere Ehrenmeldung. Er war ein außergewöhnlicher paralympischer Athlet, Gewinner einer Bronze in Lillehammer 1994."
        }
      },
      {
        title: {
          it: "L'atleta polivalente:",
          en: "The versatile athlete:",
          de: "Der vielseitige Athlet:"
        },
        text: {
          it: "Non solo sci di fondo e biathlon: Vinante ha gareggiato ad alti livelli anche nel nuoto arrivando a vincere l'argento nella staffetta mista e la medaglia di bronzo nei 100 dorso ai mondiali di nuoto di Stoke Mandeville ENG del 1986.",
          en: "Not only cross-country skiing and biathlon: Vinante also competed at high levels in swimming, winning silver in the mixed relay and the bronze medal in the 100 backstroke at the Stoke Mandeville ENG 1986 swimming world championships.",
          de: "Nicht nur Skilanglauf und Biathlon: Vinante trat auch auf hohem Niveau im Schwimmen an und gewann Silber in der gemischten Staffel und Bronze über 100 m Rücken bei den Weltmeisterschaften 1986 in Stoke Mandeville (ENG)."
        }
      },
      {
        title: {
          it: "Il Commissario Tecnico:",
          en: "The Technical Commissioner:",
          de: "Der Technische Kommissar:"
        },
        text: {
          it: "Non solo atleta: Ernesto è stato CT della nazionale italiana alle paralimpiadi di Salt Lake City 2002 e Torino 2006, ruolo in cui si è distinto per dedizione, impegno e responsabilità",
          en: "Not only an athlete: Ernesto was the head coach of the Italian national team at the Salt Lake City 2002 and Turin 2006 Paralympics, a role in which he distinguished himself for dedication, commitment and responsibility.",
          de: "Nicht nur ein Athlet: Ernesto war bei den Paralympics 2002 in Salt Lake City und 2006 in Turin Cheftrainer der italienischen Nationalmannschaft, eine Rolle, in der er sich durch Hingabe, Engagement und Verantwortung auszeichnete."
        }
      },
      {
        title: {
          it: "L'impegno civico:",
          en: "Civic commitment:",
          de: "Bürgerliches Engagement:"
        },
        text: {
          it: "A differenza di molti atleti che rimangono solo nel mondo sportivo, \"Ernestino\" è stato molto attivo nella vita sociale e politica della Val di Fiemme (è stato assessore), dimostrando che lo sport è stato un trampolino per una integrazione completa e attiva nella comunità.",
          en: "Unlike many athletes who remain only in the sports world, \"Ernestino\" was very active in the social and political life of Val di Fiemme (he was a councilor), demonstrating that sport was a springboard for a complete and active integration in the community.",
          de: "Im Gegensatz zu vielen Athleten, die nur in der Sportwelt bleiben, war \"Ernestino\" sehr aktiv im sozialen und politischen Leben des Val di Fiemme (er war Stadtrat), was zeigt, dass Sport ein Sprungbrett für eine vollständige und aktive Integration in die Gemeinschaft war."
        }
      }
    ]
  },

  // ========== BICE VANZETTA ==========
  {
    id: "8",
    name: "Bice Vanzetta",
    sport: SPORTS.CROSS_COUNTRY,
    birthDate: {
      it: "7 marzo 1961",
      en: "March 7, 1961",
      de: "7. März 1961"
    },
    birthPlace: PLACES.CAVALESE_TN,
    photo_url: "/resources/Bice_Vanzetta.webp",
    medals: { gold: 0, silver: 0, bronze: 2 },
    biography: {
      it: "Sorella di Giorgio, ha vinto due bronzi olimpici in staffetta (1992, 1994).",
      en: "Sister of Giorgio, won two Olympic bronze medals in relay (1992, 1994).",
      de: "Schwester von Giorgio, gewann zwei olympische Bronzemedaillen in der Staffel (1992, 1994)."
    },
    olympic_games: [
      { year: 1988, location: "Calgary, CAN", results: { it: "10 km classico: 17°", en: "10 km classic: 17th", de: "10 km klassisch: 17." } },
      { year: 1992, location: "Albertville, FRA", results: { it: "5 km classico: 28°", en: "5 km classic: 28th", de: "5 km klassisch: 28." } },
      { year: 1992, location: "Albertville, FRA", results: { it: "10 km pursuit: 20°", en: "10 km pursuit: 20th", de: "10 km Verfolgung: 20." } },
      { year: 1992, location: "Albertville, FRA", results: { it: "Staffetta 4×5 km: 3° (Bronzo) (con Di Centa, Paruzzi, Belmondo)", en: "4×5 km Relay: 3rd (Bronze) (with Di Centa, Paruzzi, Belmondo)", de: "4×5 km Staffel: 3. (Bronze) (mit Di Centa, Paruzzi, Belmondo)" } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "5 km classico: 19°", en: "5 km classic: 19th", de: "5 km klassisch: 19." } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "15 km pursuit: 34°", en: "15 km pursuit: 34th", de: "15 km Verfolgung: 34." } },
      { year: 1994, location: "Lillehammer, NOR", results: { it: "Staffetta 4×5 km: 3° (Bronzo) (con Di Centa, Paruzzi, Belmondo)", en: "4×5 km Relay: 3rd (Bronze) (with Di Centa, Paruzzi, Belmondo)", de: "4×5 km Staffel: 3. (Bronze) (mit Di Centa, Paruzzi, Belmondo)" } }
    ],
    achievements: [
      { it: "1987 - Mondiali (Oberstdorf, GER): 5 km - 13°", en: "1987 - World Championships (Oberstdorf, GER): 5 km - 13th", de: "1987 - WM (Oberstdorf, GER): 5 km - 13." },
      { it: "1987 - Mondiali (Oberstdorf, GER): 20 km - 13°", en: "1987 - World Championships (Oberstdorf, GER): 20 km - 13th", de: "1987 - WM (Oberstdorf, GER): 20 km - 13." },
      { it: "1987 - Mondiali (Oberstdorf, GER): Staffetta 4×5 km - 5°", en: "1987 - World Championships (Oberstdorf, GER): 4×5 km Relay - 5th", de: "1987 - WM (Oberstdorf, GER): 4×5 km Staffel - 5." },
      { it: "1991 - Mondiali (Val di Fiemme, ITA): 5 km - 12°", en: "1991 - World Championships (Val di Fiemme, ITA): 5 km - 12th", de: "1991 - WM (Val di Fiemme, ITA): 5 km - 12." },
      { it: "1991 - Mondiali (Val di Fiemme, ITA): Staffetta 4×5 km - 2° (Argento) (con Di Centa, Paruzzi, Belmondo)", en: "1991 - World Championships (Val di Fiemme, ITA): 4×5 km Relay - 2nd (Silver) (with Di Centa, Paruzzi, Belmondo)", de: "1991 - WM (Val di Fiemme, ITA): 4×5 km Staffel - 2. (Silber) (mit Di Centa, Paruzzi, Belmondo)" },
      { it: "1993 - Mondiali (Falun, SWE): 5 km - 22°", en: "1993 - World Championships (Falun, SWE): 5 km - 22nd", de: "1993 - WM (Falun, SWE): 5 km - 22." },
      { it: "1993 - Mondiali (Falun, SWE): 15 km - 26°", en: "1993 - World Championships (Falun, SWE): 15 km - 26th", de: "1993 - WM (Falun, SWE): 15 km - 26." },
      { it: "1993 - Mondiali (Falun, SWE): Pursuit - 29°", en: "1993 - World Championships (Falun, SWE): Pursuit - 29th", de: "1993 - WM (Falun, SWE): Verfolgung - 29." },
      { it: "1993 - Mondiali (Falun, SWE): 30 km - 33°", en: "1993 - World Championships (Falun, SWE): 30 km - 33rd", de: "1993 - WM (Falun, SWE): 30 km - 33." },
      { it: "1993 - Mondiali (Falun, SWE): Staffetta 4×5 km - 2° (Argento) (con Di Centa, Paruzzi, Belmondo)", en: "1993 - World Championships (Falun, SWE): 4×5 km Relay - 2nd (Silver) (with Di Centa, Paruzzi, Belmondo)", de: "1993 - WM (Falun, SWE): 4×5 km Staffel - 2. (Silber) (mit Di Centa, Paruzzi, Belmondo)" }
    ],
    curiosities: [
      {
        title: {
          it: "Fratelli d'Olimpia:",
          en: "Olympic siblings:",
          de: "Olympische Geschwister:"
        },
        text: {
          it: "Una curiosità statistica incredibile per un paese piccolo come Ziano: Bice e Giorgio sono una delle pochissime coppie di fratello e sorella ad aver vinto entrambi medaglie olimpiche nello stesso sport e nelle stesse edizioni (Albertville '92 e Lillehammer '94).",
          en: "An incredible statistical curiosity for a small town like Ziano: Bice and Giorgio are one of the very few brother and sister pairs to have both won Olympic medals in the same sport and in the same editions (Albertville '92 and Lillehammer '94).",
          de: "Eine unglaubliche statistische Kuriosität für eine kleine Stadt wie Ziano: Bice und Giorgio sind eines der ganz wenigen Bruder-Schwester-Paare, die beide olympische Medaillen in der gleichen Sportart und bei den gleichen Ausgaben gewonnen haben (Albertville '92 und Lillehammer '94)."
        }
      },
      {
        title: {
          it: "Stile impeccabile:",
          en: "Impeccable style:",
          de: "Tadelloser Stil:"
        },
        text: {
          it: "Bice era nota tecnicamente per avere uno degli stili più eleganti del circuito femminile. Mentre molte atlete puntavano sulla pura potenza fisica, lei era un esempio di scorrevolezza sugli sci, spesso studiata dai tecnici per la pulizia del gesto.",
          en: "Bice was technically known for having one of the most elegant styles on the women's circuit. While many athletes focused on pure physical power, she was an example of smoothness on skis, often studied by technicians for the cleanliness of her movements.",
          de: "Bice war technisch dafür bekannt, einen der elegantesten Stile im Damenzirkus zu haben. Während viele Athletinnen auf pure körperliche Kraft setzten, war sie ein Beispiel für Geschmeidigkeit auf den Skiern, oft von Technikern wegen der Sauberkeit ihrer Bewegungen studiert."
        }
      },
      {
        title: {
          it: "Pioniera:",
          en: "Pioneer:",
          de: "Pionierin:"
        },
        text: {
          it: "Bice è stata fondamentale per il movimento femminile. Prima di lei, della Belmondo e della Di Centa, il fondo femminile italiano faticava a emergere. Il suo bronzo in staffetta ad Albertville 1992 fu la scintilla che fece capire alle ragazze italiane che si poteva battere lo strapotere russo e scandinavo.",
          en: "Bice was fundamental for the women's movement. Before her, Belmondo and Di Centa, Italian women's cross-country skiing struggled to emerge. Her relay bronze at Albertville 1992 was the spark that made Italian girls realize they could beat Russian and Scandinavian dominance.",
          de: "Bice war grundlegend für die Frauenbewegung. Vor ihr, Belmondo und Di Centa, hatte der italienische Damen-Langlauf Mühe aufzutauchen. Ihre Staffel-Bronze in Albertville 1992 war der Funke, der italienischen Mädchen klarmachte, dass man die russische und skandinavische Dominanz schlagen konnte."
        }
      }
    ]
  },

  // ========== LIDIA TRETTEL ==========
  {
    id: "9",
    name: "Lidia Trettel",
    sport: SPORTS.SNOWBOARD,
    birthDate: {
      it: "5 aprile 1973",
      en: "April 5, 1973",
      de: "5. April 1973"
    },
    birthPlace: PLACES.CAVALESE_TN,
    photo_url: "/resources/Lidia_Trettel.webp",
    medals: { gold: 0, silver: 0, bronze: 1 },
    biography: {
      it: "Bronzo a Salt Lake City 2002, prima medaglia olimpica femminile per lo snowboard italiano.",
      en: "Bronze at Salt Lake City 2002, first female Olympic medal for Italian snowboarding.",
      de: "Bronze in Salt Lake City 2002, erste olympische Medaille für das italienische Snowboarden."
    },
    olympic_games: [
      { year: 1998, location: "Nagano, JPN", results: { it: "Slalom Gigante: 4°", en: "Giant Slalom: 4th", de: "Riesenslalom: 4." } },
      { year: 2002, location: "Salt Lake City, USA", results: { it: "Slalom Gigante Parallelo: 3° (Bronzo)", en: "Parallel Giant Slalom: 3rd (Bronze)", de: "Parallel-Riesenslalom: 3. (Bronze)" } }
    ],
    achievements: [
      { it: "1996 - Mondiali (Lienz, AUT): Slalom Gigante - 8°", en: "1996 - World Championships (Lienz, AUT): Giant Slalom - 8th", de: "1996 - WM (Lienz, AUT): Riesenslalom - 8." },
      { it: "1997 - Mondiali (San Candido, ITA): Slalom Gigante - 9°", en: "1997 - World Championships (San Candido, ITA): Giant Slalom - 9th", de: "1997 - WM (San Candido, ITA): Riesenslalom - 9." },
      { it: "1999 - Mondiali (Berchtesgaden, GER): Slalom Gigante - 2° (Argento)", en: "1999 - World Championships (Berchtesgaden, GER): Giant Slalom - 2nd (Silver)", de: "1999 - WM (Berchtesgaden, GER): Riesenslalom - 2. (Silber)" },
      { it: "2001 - Mondiali (Madonna di Campiglio, ITA): Slalom Gigante - 10°", en: "2001 - World Championships (Madonna di Campiglio, ITA): Giant Slalom - 10th", de: "2001 - WM (Madonna di Campiglio, ITA): Riesenslalom - 10." },
      { it: "2003 - Mondiali (Kreischberg, AUT): Slalom Gigante Parallelo - 4°", en: "2003 - World Championships (Kreischberg, AUT): Parallel Giant Slalom - 4th", de: "2003 - WM (Kreischberg, AUT): Parallel-Riesenslalom - 4." },
      { it: "2005 - Mondiali (Whistler, CAN): Slalom Gigante Parallelo - 39°", en: "2005 - World Championships (Whistler, CAN): Parallel Giant Slalom - 39th", de: "2005 - WM (Whistler, CAN): Parallel-Riesenslalom - 39." }
    ],
    curiosities: [
      {
        title: {
          it: "Pietra miliare:",
          en: "Milestone:",
          de: "Meilenstein:"
        },
        text: {
          it: "La sua medaglia di bronzo a Salt Lake City 2002 nel Gigante Parallelo è stata la prima medaglia olimpica femminile in assoluto per lo snowboard italiano.",
          en: "Her bronze medal at Salt Lake City 2002 in the Parallel Giant Slalom was the first Olympic women's medal ever for Italian snowboarding.",
          de: "Ihre Bronzemedaille in Salt Lake City 2002 im Parallel-Riesenslalom war die allererste olympische Medaille für italienische Frauen im Snowboarden."
        }
      },
      {
        title: {
          it: "La fioraia canterina:",
          en: "The singing florist:",
          de: "Die singende Blumenhändlerin:"
        },
        text: {
          it: "Prima di diventare un'eroina nazionale con il Bronzo a Salt Lake City 2002, Lidia conduceva una vita molto legata al paese: lavorava come fioraia e cantava nel coro parrocchiale. I giornali dell'epoca titolarono spesso sulla \"dolce fioraia\" che diventava una \"dura\" sulla tavola.",
          en: "Before becoming a national heroine with the Bronze at Salt Lake City 2002, Lidia led a life very connected to her town: she worked as a florist and sang in the parish choir. Newspapers of the time often headlined about the \"sweet florist\" who became \"tough\" on the board.",
          de: "Bevor sie mit der Bronze in Salt Lake City 2002 zur Nationalheldin wurde, führte Lidia ein Leben, das sehr mit ihrem Ort verbunden war: Sie arbeitete als Blumenhändlerin und sang im Kirchenchor. Die Zeitungen der damaligen Zeit titelten oft über die \"süße Blumenhändlerin\", die auf dem Brett \"hart\" wurde."
        }
      },
      {
        title: {
          it: "La vendetta dei 29 centesimi:",
          en: "The revenge of 29 hundredths:",
          de: "Die Rache der 29 Hundertstel:"
        },
        text: {
          it: "La sua medaglia del 2002 fu una sorta di riscatto personale. Quattro anni prima, a Nagano 1998, era arrivata quarta, perdendo il podio per soli 29 centesimi di secondo. Quella delusione fu il carburante che la portò al successo quattro anni dopo.",
          en: "Her 2002 medal was a sort of personal redemption. Four years earlier, at Nagano 1998, she had finished fourth, losing the podium by just 29 hundredths of a second. That disappointment was the fuel that led her to success four years later.",
          de: "Ihre Medaille 2002 war eine Art persönliche Wiedergutmachung. Vier Jahre zuvor, in Nagano 1998, war sie Vierte geworden und hatte das Podium um nur 29 Hundertstelsekunden verpasst. Diese Enttäuschung war der Treibstoff, der sie vier Jahre später zum Erfolg führte."
        }
      },
      {
        title: {
          it: "L'oro morale:",
          en: "The moral gold:",
          de: "Das moralische Gold:"
        },
        text: {
          it: "Fu esclusa da Torino 2006 per un vero inferno burocratico. Mentre assisteva alle gare di Torino 2006 dalla tribuna La Gazzetta dello Sport decise di assegnarle un premio speciale: la Medaglia del Fair Play. Lei stessa ha poi commentato con ironia e saggezza: \"Posso dire di aver vinto tre medaglie diverse in tre Olimpiadi: quella di legno a Nagano (4° posto), quella di bronzo a Salt Lake City e quella del Fair Play a Torino\". Un esempio di stile che vale quanto un oro.",
          en: "She was excluded from Turin 2006 due to a real bureaucratic nightmare. While watching the Turin 2006 races from the stands, La Gazzetta dello Sport decided to give her a special award: the Fair Play Medal. She herself later commented with irony and wisdom: \"I can say I won three different medals at three Olympics: the wooden one at Nagano (4th place), the bronze at Salt Lake City and the Fair Play one at Turin\". An example of style worth as much as gold.",
          de: "Sie wurde von Turin 2006 wegen eines bürokratischen Alptraums ausgeschlossen. Während sie die Rennen von Turin 2006 von der Tribüne aus verfolgte, beschloss La Gazzetta dello Sport, ihr eine besondere Auszeichnung zu verleihen: die Fair-Play-Medaille. Sie selbst kommentierte später mit Ironie und Weisheit: \"Ich kann sagen, dass ich drei verschiedene Medaillen bei drei Olympischen Spielen gewonnen habe: die hölzerne in Nagano (4. Platz), die Bronze in Salt Lake City und die Fair-Play-Medaille in Turin\". Ein Beispiel für Stil, das so viel wert ist wie Gold."
        }
      }
    ]
  },

  // ========== ANTONELLA CONFORTOLA ==========
  {
    id: "2",
    name: "Antonella Confortola",
    sport: SPORTS.CROSS_COUNTRY,
    birthDate: {
      it: "16 ottobre 1975",
      en: "October 16, 1975",
      de: "16. Oktober 1975"
    },
    birthPlace: PLACES.CAVALESE_TN,
    photo_url: "/resources/Antonella_Confortola.webp",
    medals: { gold: 0, silver: 0, bronze: 1 },
    biography: {
      it: "Oltre a essere una fondista olimpica (bronzo a Torino 2006), è una leggenda della Corsa in Montagna (Mountain Running).",
      en: "Besides being an Olympic cross-country skier (bronze at Turin 2006), she is a legend in Mountain Running.",
      de: "Neben ihrer Karriere als olympische Langläuferin (Bronze in Turin 2006) ist sie eine Legende im Berglauf."
    },
    olympic_games: [
      { year: 1998, location: "Nagano, JPN", results: { it: "Fondo 15 km: 27°", en: "Cross-country 15 km: 27th", de: "Langlauf 15 km: 27." } },
      { year: 1998, location: "Nagano, JPN", results: { it: "Fondo 30 km: 20°", en: "Cross-country 30 km: 20th", de: "Langlauf 30 km: 20." } },
      { year: 2002, location: "Salt Lake City, USA", results: { it: "Fondo 10 km: 34°", en: "Cross-country 10 km: 34th", de: "Langlauf 10 km: 34." } },
      { year: 2002, location: "Salt Lake City, USA", results: { it: "Fondo 15 km: 16°", en: "Cross-country 15 km: 16th", de: "Langlauf 15 km: 16." } },
      { year: 2002, location: "Salt Lake City, USA", results: { it: "Fondo 30 km: 19°", en: "Cross-country 30 km: 19th", de: "Langlauf 30 km: 19." } },
      { year: 2006, location: "Torino, ITA", results: { it: "Fondo 10 km: 34°", en: "Cross-country 10 km: 34th", de: "Langlauf 10 km: 34." } },
      { year: 2006, location: "Torino, ITA", results: { it: "Fondo Inseguimento: 22°", en: "Cross-country Pursuit: 22nd", de: "Langlauf Verfolgung: 22." } },
      { year: 2006, location: "Torino, ITA", results: { it: "Staffetta 4×5 km: 3° (Bronzo)", en: "4×5 km Relay: 3rd (Bronze)", de: "4×5 km Staffel: 3. (Bronze)" } },
      { year: 2010, location: "Vancouver, CAN", results: { it: "Fondo 30 km: 16°", en: "Cross-country 30 km: 16th", de: "Langlauf 30 km: 16." } }
    ],
    achievements: [
      { it: "1999 - Mondiali (Ramsau, AUT): Fondo 15 km - 27°", en: "1999 - World Championships (Ramsau, AUT): 15 km - 27th", de: "1999 - WM (Ramsau, AUT): 15 km - 27." },
      { it: "1999 - Mondiali (Ramsau, AUT): Fondo 30 km - 39°", en: "1999 - World Championships (Ramsau, AUT): 30 km - 39th", de: "1999 - WM (Ramsau, AUT): 30 km - 39." },
      { it: "1999 - Mondiali (Ramsau, AUT): Staffetta 4×5 km - 2° (Argento)", en: "1999 - World Championships (Ramsau, AUT): 4×5 km Relay - 2nd (Silver)", de: "1999 - WM (Ramsau, AUT): 4×5 km Staffel - 2. (Silber)" },
      { it: "2003 - Mondiali (Val di Fiemme, ITA): Fondo 15 km - 22°", en: "2003 - World Championships (Val di Fiemme, ITA): 15 km - 22nd", de: "2003 - WM (Val di Fiemme, ITA): 15 km - 22." },
      { it: "2003 - Mondiali (Val di Fiemme, ITA): Fondo 30 km - 15°", en: "2003 - World Championships (Val di Fiemme, ITA): 30 km - 15th", de: "2003 - WM (Val di Fiemme, ITA): 30 km - 15." },
      { it: "2003 - Mondiali (Val di Fiemme, ITA): Staffetta 4×5 km - 7°", en: "2003 - World Championships (Val di Fiemme, ITA): 4×5 km Relay - 7th", de: "2003 - WM (Val di Fiemme, ITA): 4×5 km Staffel - 7." },
      { it: "2005 - Mondiali (Oberstdorf, GER): Fondo 10 km - 21°", en: "2005 - World Championships (Oberstdorf, GER): 10 km - 21st", de: "2005 - WM (Oberstdorf, GER): 10 km - 21." },
      { it: "2005 - Mondiali (Oberstdorf, GER): Fondo Inseguimento - 18°", en: "2005 - World Championships (Oberstdorf, GER): Pursuit - 18th", de: "2005 - WM (Oberstdorf, GER): Verfolgung - 18." },
      { it: "2005 - Mondiali (Oberstdorf, GER): Fondo 30 km - 20°", en: "2005 - World Championships (Oberstdorf, GER): 30 km - 20th", de: "2005 - WM (Oberstdorf, GER): 30 km - 20." },
      { it: "2005 - Mondiali (Oberstdorf, GER): Staffetta 4×5 km - 3° (Bronzo)", en: "2005 - World Championships (Oberstdorf, GER): 4×5 km Relay - 3rd (Bronze)", de: "2005 - WM (Oberstdorf, GER): 4×5 km Staffel - 3. (Bronze)" },
      { it: "2007 - Mondiali (Sapporo, JPN): Fondo 10 km - 23°", en: "2007 - World Championships (Sapporo, JPN): 10 km - 23rd", de: "2007 - WM (Sapporo, JPN): 10 km - 23." },
      { it: "2007 - Mondiali (Sapporo, JPN): Fondo 15 km - 35°", en: "2007 - World Championships (Sapporo, JPN): 15 km - 35th", de: "2007 - WM (Sapporo, JPN): 15 km - 35." },
      { it: "2007 - Mondiali (Sapporo, JPN): Fondo 30 km - 14°", en: "2007 - World Championships (Sapporo, JPN): 30 km - 14th", de: "2007 - WM (Sapporo, JPN): 30 km - 14." },
      { it: "2009 - Mondiali (Liberec, CZE): Fondo 15 km - 16°", en: "2009 - World Championships (Liberec, CZE): 15 km - 16th", de: "2009 - WM (Liberec, CZE): 15 km - 16." },
      { it: "2009 - Mondiali (Liberec, CZE): Fondo Inseguimento - 15°", en: "2009 - World Championships (Liberec, CZE): Pursuit - 15th", de: "2009 - WM (Liberec, CZE): Verfolgung - 15." },
      { it: "2009 - Mondiali (Liberec, CZE): Staffetta 4×5 km - 5°", en: "2009 - World Championships (Liberec, CZE): 4×5 km Relay - 5th", de: "2009 - WM (Liberec, CZE): 4×5 km Staffel - 5." },
      { it: "2011 - Mondiali (Oslo, NOR): Fondo 30 km - 9°", en: "2011 - World Championships (Oslo, NOR): 30 km - 9th", de: "2011 - WM (Oslo, NOR): 30 km - 9." },
      { it: "2011 - Mondiali (Oslo, NOR): Staffetta 4×5 km - 4°", en: "2011 - World Championships (Oslo, NOR): 4×5 km Relay - 4th", de: "2011 - WM (Oslo, NOR): 4×5 km Staffel - 4." }
    ],
    curiosities: [
      {
        title: {
          it: "La Regina della Montagna:",
          en: "The Mountain Queen:",
          de: "Die Königin der Berge:"
        },
        text: {
          it: "La curiosità più interessante su Antonella è la sua \"doppia vita\" sportiva. Oltre a essere una fondista olimpica (bronzo a Torino 2006), è una leggenda della Corsa in Montagna (Mountain Running), disciplina in cui si è laureata Campionessa del Mondo. È una delle poche atlete al mondo capace di eccellere a livelli iridati sia sulla neve che sui sentieri estivi.",
          en: "The most interesting fact about Antonella is her \"double sporting life\". Besides being an Olympic cross-country skier (bronze at Turin 2006), she is a legend in Mountain Running, a discipline in which she became World Champion. She is one of the few athletes in the world capable of excelling at world-class level both on snow and on summer trails.",
          de: "Das Interessanteste an Antonella ist ihr \"doppeltes Sportlerleben\". Neben ihrer Karriere als olympische Langläuferin (Bronze in Turin 2006) ist sie eine Legende im Berglauf (Mountain Running), einer Disziplin, in der sie Weltmeisterin wurde. Sie ist eine der wenigen Athletinnen der Welt, die auf Weltklasseniveau sowohl auf Schnee als auch auf Sommerpfaden brillieren kann."
        }
      },
      {
        title: {
          it: "La \"Mini-Marcialonga\" e la lezione della pazienza:",
          en: "The \"Mini-Marcialonga\" and the lesson of patience:",
          de: "Die \"Mini-Marcialonga\" und die Lektion der Geduld:"
        },
        text: {
          it: "A soli 10 anni doveva partecipare alla Mini-Marcialonga, la gara per i giovanissimi, ma si ammalò proprio il giorno prima. \"Per me fu un dramma assoluto, come se avessi perso un'Olimpiade\", ricorda oggi ridendo. Quell'episodio, unito a una polmonite che la colpì anni dopo prima di un Mondiale, ha forgiato una delle sue qualità umane più forti: la capacità di accettare gli imprevisti. Antonella è nota per non essersi mai fatta abbattere dalla sfortuna, trasformando ogni stop forzato in un'occasione per ricaricare le energie mentali.",
          en: "At just 10 years old she was supposed to participate in the Mini-Marcialonga, the race for the very young, but she fell ill just the day before. \"For me it was an absolute drama, as if I had lost an Olympics\", she remembers today laughing. That episode, combined with pneumonia that struck her years later before a World Championship, forged one of her strongest human qualities: the ability to accept the unexpected. Antonella is known for never letting bad luck get her down, transforming every forced stop into an opportunity to recharge her mental energies.",
          de: "Mit nur 10 Jahren sollte sie an der Mini-Marcialonga, dem Rennen für die Jüngsten, aber sie wurde genau am Tag davor krank. \"Für mich war es ein absolutes Drama, als hätte ich eine Olympiade verloren\", erinnert sie sich heute lachend. Diese Episode, zusammen mit einer Lungenentzündung, die sie Jahre später vor einer Weltmeisterschaft traf, schmiedete eine ihrer stärksten menschlichen Qualitäten: die Fähigkeit, das Unerwartete zu akzeptieren. Antonella ist bekannt dafür, sich nie vom Pech unterkriegen zu lassen und jeden erzwungenen Stopp in eine Gelegenheit zu verwandeln, ihre mentalen Energien aufzuladen."
        }
      },
      {
        title: {
          it: "La Regina della Scalata:",
          en: "The Queen of the Climb:",
          de: "Die Königin des Anstiegs:"
        },
        text: {
          it: "Se c'è una pista che incute timore a qualunque fondista al mondo, è l'Alpe del Cermis, la salita finale del Tour de Ski con pendenze che sfiorano il 30%. Antonella, grazie alla sua struttura leggera da scalatrice pura, è diventata una leggenda su quelle rampe. La curiosità è che, nonostante si fosse già ritirata dalle competizioni d'élite, ha continuato a partecipare alla \"Rampa con i Campioni\" (la versione per amatori e vecchie glorie) dominandola sistematicamente e facendo segnare tempi che avrebbero fatto sfigurare molte atlete ancora in attività.",
          en: "If there is a track that strikes fear into any cross-country skier in the world, it is the Alpe del Cermis, the final climb of the Tour de Ski with gradients approaching 30%. Antonella, thanks to her light structure as a pure climber, has become a legend on those ramps. The curiosity is that, despite having already retired from elite competitions, she continued to participate in the \"Rampa con i Campioni\" (the version for amateurs and old glories) dominating it systematically and setting times that would have made many active athletes look bad.",
          de: "Wenn es eine Strecke gibt, die jedem Langläufer der Welt Angst einjagt, ist es die Alpe del Cermis, der letzte Anstieg der Tour de Ski mit Steigungen von fast 30%. Antonella ist dank ihrer leichten Struktur als reine Kletterin zu einer Legende auf diesen Rampen geworden. Das Kuriose ist, dass sie, obwohl sie sich bereits von den Elite-Wettkämpfen zurückgezogen hatte, weiterhin an der \"Rampa con i Campioni\" (der Version für Amateure und alte Ruhmesblätter) teilnahm, sie systematisch dominierte und Zeiten setzte, die viele aktive Athletinnen schlecht aussehen ließen."
        }
      }
    ]
  }

];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extracts the value for a specific language from a multilingual object
 * @param {Object|string} obj - The multilingual object or string
 * @param {string} lang - The language code (it, en, de)
 * @returns {string} The value for the specified language
 */
const getLocalizedValue = (obj, lang) => {
  if (typeof obj === 'string') return obj;
  if (obj && typeof obj === 'object') {
    return obj[lang] || obj['it'] || '';
  }
  return '';
};

/**
 * Flattens an athlete object for a specific language
 * @param {Object} athlete - The athlete data with multilingual fields
 * @param {string} lang - The language code
 * @returns {Object} The flattened athlete object
 */
const flattenAthlete = (athlete, lang) => {
  return {
    ...athlete,
    sport: getLocalizedValue(athlete.sport, lang),
    birthDate: getLocalizedValue(athlete.birthDate, lang),
    birth_date: getLocalizedValue(athlete.birthDate, lang), // alias for compatibility
    birthPlace: getLocalizedValue(athlete.birthPlace, lang),
    biography: getLocalizedValue(athlete.biography, lang),
    olympic_games: athlete.olympic_games.map(g => ({
      year: g.year,
      location: getLocalizedValue(g.location, lang),
      results: getLocalizedValue(g.results, lang)
    })),
    achievements: athlete.achievements.map(a => getLocalizedValue(a, lang)),
    curiosities: athlete.curiosities.map(c => ({
      title: getLocalizedValue(c.title, lang),
      text: getLocalizedValue(c.text, lang)
    }))
  };
};

/**
 * Gets all athletes flattened for a specific language
 * @param {string} lang - The language code
 * @returns {Array} Array of flattened athlete objects
 */
const getAthletes = (lang = 'it') => {
  return athletesData.map(athlete => flattenAthlete(athlete, lang));
};

// ============================================================================
// API SIMULATION
// ============================================================================

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class Base44EntityManager {
  constructor(entityName) {
    this.entityName = entityName;
  }

  async list(lang = 'it') {
    await delay(300);
    return getAthletes(lang);
  }

  async get(id, lang = 'it') {
    await delay(200);
    const athletes = getAthletes(lang);
    return athletes.find(item => item.id === id);
  }

  async filter(criteria, lang = 'it') {
    await delay(200);
    const athletes = getAthletes(lang);
    return athletes.filter(item => {
      return Object.entries(criteria).every(([key, value]) => {
        return item[key] == value;
      });
    });
  }

  async search(query, lang = 'it') {
    await delay(250);
    const athletes = getAthletes(lang);
    const searchLower = query.toLowerCase();
    return athletes.filter(item => {
      return Object.values(item).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchLower);
        }
        return false;
      });
    });
  }

  async create(data) {
    await delay(300);
    return { ...data, id: String(Date.now()) };
  }

  async update(id, data) {
    await delay(300);
    return { id, ...data };
  }

  async delete(id) {
    await delay(250);
    return { id };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const base44 = {
  entities: {
    Athlete: new Base44EntityManager('Athlete'),
  },
  
  async healthCheck() {
    await delay(100);
    return { status: 'ok', timestamp: Date.now() };
  },
};

// Export raw data for direct access if needed
export { athletesData };

export default base44;
